<template>
  <div class="date-picker">
    <div
      v-if="title"
      class="title-date-picker"
    >
      <span>{{ title }}</span>
    </div>

    <div
      class="content-calendar-input"
      :class="{ 'disabled-component': disabled }"
    >
      <input
        ref="inputCalendarRef"
        class="input-calendar"
        type="text"
        :placeholder="t('common.date')"
        readonly
      >

      <div
        class="input-calendar-icon"
        @click="handleButtonClick"
      >
        <img
          v-show="componentMode === states.SEARCH"
          class="input-calendar-icon__search"
          src="@/static/icons/ic_date_select.svg"
          width="20px"
          height="20px"
          alt="iconSelect"
        >

        <img
          v-show="componentMode === states.VALIDATED"
          class="input-calendar-icon__cancel"
          src="@/static/icons/ic_cancel.svg"
          width="12px"
          height="12px"
          alt="iconCancel"
        >
      </div>
    </div>
  </div>
</template>

<script setup>
// @ts-check
import {
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue';
import { f7 } from 'framework7-vue';
import { useI18n } from 'vue-i18n';

/** @import { PropType, Ref, ShallowRef } from 'vue' */

const props = defineProps({
  /** Label text that will be displayed above the input. */
  title: {
    type: String,
    default: undefined,
  },
  /**
   * Default value that will be set when component is mounted or when value is reset.
   *
   * When in non-range picker mode, it should be `Date` or `null`.
   * When in range picker mode, it should be `Date[]` or `null`.
   */
  defaultValue: {
    /** @type {PropType<Date | Date[] | null>} */
    type: [Date, Array],
    default: () => new Date(),
  },
  /**
   * If undefined, it will use format based on `locale` (or browser locale).
   *
   * It can accept `Intl.DateTimeFormat`.`options`.
   *
   * For example `{ month: 'long', day: 'numeric' }`.
   */
  dateFormat: {
    type: /** @type {PropType<string | Intl.DateTimeFormatOptions>} */ (
      [String, Object]
    ),
    default: 'dd/mm/yyyy',
  },
  /**
   * Can be `auto`, `popover` (to open calendar in popover), `sheet` (to open in sheet modal) or `customModal` (to open in custom Calendar modal overlay).
   * In case of `auto` will open in sheet modal on small screens and in popover on large screens.
   */
  openIn: {
    type: /** @type {PropType<'auto' | 'popover' | 'sheet' | 'customModal'>} */ (
      String
    ),
    default: 'customModal',
    validator: (value) => typeof value === 'string' && ['auto', 'popover', 'sheet', 'customModal'].includes(value),
  },
  /** Whether to disable the intercation with the component. */
  disabled: {
    type: Boolean,
    default: false,
  },
  /** Whether to init the picker with null value when component is mounted. */
  initNull: {
    type: Boolean,
    default: false,
  },
  /** Minimum allowed date. */
  minDate: {
    type: Date,
    default: null,
  },
  /** Maximum allowed date. */
  maxDate: {
    type: Date,
    default: null,
  },
  /** Enables range picker. */
  rangePicker: {
    type: Boolean,
    default: false,
  },
  /** Enables time picker. */
  timePicker: {
    type: Boolean,
    default: false,
  },
  /**
   * Time format displayed in time selector. It accepts `Intl.DateTimeFormat`.`options`.
   *
   * `AM/PM` format depends on selected specified locale, or on browser locale if not specified.
   */
  timePickerFormat: {
    type: /** @type {PropType<Intl.DateTimeFormatOptions>} */ (
      Object
    ),
    default: undefined,
  },
});

const emit = defineEmits([
  'onchange',
]);

const { t } = useI18n();
/** @type {ShallowRef<HTMLInputElement | undefined>} */
const inputCalendarRef = shallowRef(undefined);
/** @type {Ref<ReturnType<typeof f7.calendar.create> | undefined>} */
const calendarElement = ref(undefined);
/** @type {Ref<Date | { Init: Date, End: Date } | null>} */
const modelValue = ref(null);
const shouldInitNull = ref(props.initNull);

const states = {
  VALIDATED: 'VALIDATED',
  SEARCH: 'SEARCH',
};
const componentMode = ref('');

watch(() => props.initNull, (val) => {
  shouldInitNull.value = val;
});

function getInitialValues() {
  if (!props.defaultValue || shouldInitNull.value) {
    return undefined;
  }

  const { defaultValue } = props;
  const isDefaultValueArray = Array.isArray(defaultValue);

  if (!props.rangePicker && isDefaultValueArray) {
    throw new Error(
      'The defaultValue prop should be a Date or null when in non-range picker mode.',
    );
  }

  if (!props.rangePicker && !isDefaultValueArray) {
    const defaultDate = new Date(defaultValue);
    // Quitamos las horas minutos y segundos para las futuras comparaciones.
    defaultDate.setHours(0, 0, 0, 0);
    return [defaultDate];
  }

  if (props.rangePicker && isDefaultValueArray) {
    const [initDate, endDate] = defaultValue;
    if (initDate && endDate) {
      const initDateCopy = new Date(initDate);
      initDateCopy.setHours(0, 0, 0, 0);
      const endDateCopy = new Date(endDate);
      endDateCopy.setHours(0, 0, 0, 0);

      return [initDateCopy, endDateCopy];
    }

    if (initDate) {
      const initDateCopy = new Date(initDate);
      initDate.setHours(0, 0, 0, 0);

      return [initDateCopy, initDate];
    }

    return [];
  }

  if (props.rangePicker && !isDefaultValueArray) {
    return [defaultValue, defaultValue];
  }

  return undefined;
}

/**
 * @param {Date[] | undefined} values
 * @returns {void}
 */
function setModelValueInitialValues(values) {
  if (!values) {
    modelValue.value = null;
    return;
  }

  if (values.length === 1 && values[0]) {
    modelValue.value = values[0];
    return;
  }

  if (values.length === 2 && values[0] && values[1]) {
    modelValue.value = {
      Init: values[0],
      End: values[1],
    };
  }
}

/**
 * @public
 * @returns {void}
 */
function initialize() {
  setModelValueInitialValues(getInitialValues());
  createCalendar();

  if (modelValue.value) {
    componentMode.value = states.VALIDATED;
    emitChange(modelValue.value);
  } else {
    componentMode.value = states.SEARCH;
  }
}

async function createCalendar() {
  const initialValues = getInitialValues();
  setModelValueInitialValues(initialValues);

  calendarElement.value = f7.calendar.create({
    inputEl: inputCalendarRef.value,
    minDate: props.minDate,
    maxDate: props.maxDate,
    openIn: props.openIn,
    header: false,
    footer: false,
    closeOnSelect: true,
    rangePicker: props.rangePicker,
    dateFormat: props.dateFormat,
    timePicker: props.timePicker,
    timePickerFormat: props.timePickerFormat,
    value: initialValues,
    on: {
      opened() {
        if (props.defaultValue) {
          shouldInitNull.value = false;
        }
      },
      closed({ value }) {
        if (props.defaultValue) {
          shouldInitNull.value = false;
        }

        if (value) {
          if (props.rangePicker) {
            if (value[0] && !value[1]) {
              // This line makes the input show two values, as it should
              calendarElement.value?.addValue(value[0]);

              closeCalendarRange(value[0], value[0]);
            } else if (value[0] && value[1]) {
              closeCalendarRange(value[0], value[1]);
            }
          } else {
            const newValue = value[0] ?? null;
            closeCalendar(newValue);
          }
        }
      },
    },
  });
}

/**
 * @param {Date | null} newDate
 * @returns {void}
 */
function closeCalendar(newDate) {
  componentMode.value = states.VALIDATED;
  modelValue.value = newDate;
  emitChange(newDate);
}

/**
 * @param {Date | { Init: Date, End: Date } | null} newDate
 * @returns {void}
 */
function emitChange(newDate) {
  emit('onchange', newDate);
}

/**
 * @param {Date} init
 * @param {Date} end
 * @returns {void}
 */
function closeCalendarRange(init, end) {
  componentMode.value = states.VALIDATED;
  changeValueRange(init, end);
}

/**
 * @param {Date} init
 * @param {Date} end
 * @returns {void}
 */
function changeValueRange(init, end) {
  modelValue.value = init && end ? { Init: init, End: end } : null;
  emit('onchange', modelValue.value);
}

/**
 * @param {Date | { Init: Date; End: Date } | null} date
 * @returns {Date[]}
 */
function getValuesFromDate(date) {
  if (!date) {
    return [];
  }

  if (date instanceof Date) {
    return [date];
  }

  return [date.Init, date.End];
}

/**
 * @public
 * @param {Date | { Init: Date; End: Date } | null} newDate
 * @returns {void}
 */
function setDate(newDate) {
  modelValue.value = newDate;
  calendarElement.value?.setValue(getValuesFromDate(newDate));

  emitChange(newDate);
}

/**
 * Resets the date picker to the current date.
 * @public
 * @param {boolean} emitEvent - Whether to emit the `onchange` event.
 * @returns {void}
 */
function resetToCurrentDate(emitEvent = true) {
  modelValue.value = new Date();
  calendarElement.value?.setValue([modelValue.value]);

  if (emitEvent) {
    emitChange(modelValue.value);
  }
}

/**
 * Resets the date picker to the null value.
 * @public
 * @param {boolean} emitEvent - Whether to emit the `onchange` event.
 * @returns {void}
 */
function resetToNullValue(emitEvent = true) {
  modelValue.value = null;
  calendarElement.value?.setValue([]);

  if (emitEvent) {
    emitChange(new Date());
  }
}

function handleButtonClick() {
  if (componentMode.value === states.SEARCH) {
    const element = calendarElement.value;
    if (element && !element.opened) {
      element.open();
    }
  } else if (componentMode.value === states.VALIDATED) {
    clear();
  }
}

function clear() {
  const element = calendarElement.value;
  if (!element) {
    return;
  }

  componentMode.value = states.SEARCH;

  if (element) {
    element.setValue([]);
    element.destroy();
  }

  createCalendar();
  emitChange(modelValue.value);
}

onMounted(() => {
  initialize();
});

defineExpose({
  clear,
  initialize,
  resetToCurrentDate,
  resetToNullValue,
  setDate,
});
</script>

<style scoped lang="scss">
.disabled-component {
  opacity: 0.6;
  pointer-events: none;
}

.date-picker {
  --f7-block-margin-vertical: 15px;
  --f7-block-padding-horizontal: 16px;
}

.title-date-picker {
  font-size: var(--date-picker-label-font-size);
  color: var(--date-picker-label-color);
  line-height: var(--date-picker-label-line-height);
  margin: var(--date-picker-label-margin);
}

.content-calendar-input {
  display: flex;
  flex-direction: row;
  background-color: var(--date-picker-input-bg-color);
  border-radius: var(--date-picker-input-border-radius);
  border: var(--date-picker-input-border);
  box-sizing: border-box;

  input {
    height: var(--date-picker-input-height);
    color: var(--date-picker-input-color);
    font-size: var(--date-picker-input-font-size);
    font-weight: var(--date-picker-input-font-weight);
    padding: var(--date-picker-input-padding);
  }

  .input-calendar {
    width: calc(100% - 30px);
  }

  .input-calendar-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    box-sizing: border-box;
    min-width: var(--date-picker-btn-min-width);
    border-left: var(--date-picker-btn-border-left);
    background: var(--date-picker-btn-bg-color);
    border-top-right-radius: 7px;
    border-bottom-right-radius: 7px;
    cursor: pointer;

    &__search {
      width: var(--date-picker-btn-search-img-size);
      height: var(--date-picker-btn-search-img-size);
    }

    &__cancel {
      width: var(--date-picker-btn-cancel-img-size);
      height: var(--date-picker-btn-cancel-img-size);
    }
  }
}
</style>
